import { useEffect, useState } from "react";
import { http } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Plus, Trash2, Copy, ListOrdered } from "lucide-react";
import { OrderDialog } from "@/components/OrderDialog";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { PhotoField } from "@/components/UserAvatar";
import { UserTree } from "@/components/UserTree";
import { BulkPermsDialog } from "@/components/BulkPermsDialog";
import { AccessEditor } from "@/components/AccessEditor";
import { SCOPE_BASE, SCOPE_LABELS } from "@/lib/helpers";
import { useBadges } from "@/components/Badges";

const emptyForm = { username: "", password: "", full_name: "", email: "", role: "personel", custom_role_id: "", branch_id: "", permissions: {}, photo_path: "" };

export default function Users() {
  const [items, setItems] = useState([]);
  const [branches, setBranches] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [selected, setSelected] = useState(new Set());
  const [ordering, setOrdering] = useState(false);
  const [roles, setRoles] = useState([]);
  const badges = useBadges();

  const load = () => http.get("/users").then((r) => { setItems(r.data); setSelected(new Set()); });
  useEffect(() => {
    load();
    http.get("/branches").then((r) => setBranches(r.data));
    http.get("/roles").then((r) => setRoles(r.data));
  }, []);

  const openNew = (preset = {}) => { setEditing(null); setForm({ ...emptyForm, ...preset }); setOpen(true); };
  const openEdit = (u) => { setEditing(u); setForm({ ...u, password: "", branch_id: u.branch_id || "", permissions: u.permissions || {}, photo_path: u.photo_path || "", custom_role_id: u.custom_role_id || "" }); setOpen(true); };
  const customRole = roles.find((r) => r.id === form.custom_role_id);
  const pickRole = (v) => {
    if (v.startsWith("c:")) {
      const r = roles.find((x) => x.id === v.slice(2));
      setForm({ ...form, custom_role_id: r.id, role: SCOPE_BASE[r.scope] });
    } else setForm({ ...form, custom_role_id: "", role: v });
  };

  const save = async () => {
    if (form.password && !/^\d{6}$/.test(form.password)) { toast.error("Şifre 6 haneli ve sadece rakam olmalı"); return; }
    if (!form.username || (!editing && !form.password) || !form.full_name) {
      toast.error("Kullanıcı adı, isim ve şifre zorunlu"); return;
    }
    try {
      const payload = {
        full_name: form.full_name,
        email: form.email || null,
        role: form.role,
        custom_role_id: form.custom_role_id || "",
        branch_id: form.branch_id || null,
        permissions: form.permissions || {},
        photo_path: form.photo_path || "",
      };
      if (form.password) payload.password = form.password;
      if (editing && form.username !== editing.username) payload.username = form.username;
      if (editing) {
        await http.put(`/users/${editing.id}`, payload);
      } else {
        await http.post("/users", { ...payload, username: form.username, password: form.password });
      }
      toast.success("Kaydedildi"); setOpen(false); load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Hata");
    }
  };

  const toggleActive = async (u) => {
    await http.put(`/users/${u.id}`, { is_active: !u.is_active });
    load();
  };

  const [resetInfo, setResetInfo] = useState(null);
  const { user: me } = useAuth();
  const [bulkPerms, setBulkPerms] = useState(false);
  const [showPins, setShowPins] = useState(false);
  const [pins, setPins] = useState({});
  const [allPins, setAllPins] = useState(null);
  useEffect(() => { if (showPins) http.get("/users/pins").then((r) => setPins(r.data)); }, [showPins, items]);
  const resetAll = async () => {
    if (!window.confirm("TÜM kullanıcılara (sizin dahil) yeni 6 haneli PIN verilecek ve eski şifreler çalışmayacak. Devam edilsin mi?")) return;
    try { const { data } = await http.post("/users/reset-all-pins"); setAllPins(data); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Yapılamadı"); }
  };
  const resetPw = async (u) => {
    if (!window.confirm(`"${u.full_name}" için yeni şifre oluşturulsun mu? Eski şifre geçersiz olur.`)) return;
    try {
      const { data } = await http.post(`/users/${u.id}/reset-password`);
      setResetInfo(data);
    } catch { toast.error("Şifre sıfırlanamadı"); }
  };

  const remove = async (u) => {
    if (!window.confirm(`"${u.full_name}" kullanıcısı silinsin mi?`)) return;
    await http.delete(`/users/${u.id}`);
    toast.success("Silindi"); load();
  };

  const bulkDelete = async () => {
    if (selected.size === 0) return;
    const ids = Array.from(selected);
    try {
      const { data } = await http.post("/users/bulk-delete", { ids });
      toast.success(`${data.deleted} kullanıcı silindi`);
      load();
    } catch { toast.error("Silinemedi"); }
  };

  const toggleSelect = (id) => {
    const s = new Set(selected);
    if (s.has(id)) s.delete(id); else s.add(id);
    setSelected(s);
  };
  const toggleAll = () => {
    if (selected.size === items.length) setSelected(new Set());
    else setSelected(new Set(items.map((u) => u.id)));
  };


  return (
    <div className="space-y-6">
      <header className="animate-fade-up flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-display text-3xl font-bold">Kullanıcılar</h1>
          <p className="text-slate-400 text-sm mt-1">{items.length} kullanıcı</p>
        </div>
        <div className="flex gap-2 w-full sm:w-auto">
          <Button variant="outline" onClick={toggleAll} className="border-slate-700" data-testid="user-select-all">
            {selected.size === items.length && items.length ? "Seçimi Kaldır" : "Tümünü Seç"}
          </Button>
          {selected.size > 0 && (
            <Button onClick={() => setBulkPerms(true)} className="bg-blue-600 hover:bg-blue-500" data-testid="user-bulk-perms">Yetki Değiştir ({selected.size})</Button>
          )}
          <Button variant="outline" onClick={() => setShowPins(!showPins)} className="border-slate-700" data-testid="user-toggle-pins">
            {showPins ? "PIN'leri Gizle" : "PIN'leri Göster"}
          </Button>
          {me?.role === "koordinator" && (
            <Button variant="outline" onClick={resetAll} className="border-amber-500/50 text-amber-200" data-testid="user-reset-all-pins">Herkese Yeni PIN</Button>
          )}
          {selected.size > 0 && (
            <Button variant="destructive" onClick={bulkDelete} className="bg-red-600 hover:bg-red-500" data-testid="user-bulk-delete">
              <Trash2 size={14} className="mr-1" /> {selected.size} Sil
            </Button>
          )}
          <Button variant="outline" className="border-slate-700" onClick={() => setOrdering(true)} data-testid="user-order-btn">
            <ListOrdered size={15} className="mr-1" /> Sırala
          </Button>
          <Button onClick={() => openNew()} className="bg-blue-600 hover:bg-blue-500" data-testid="user-new">
            <Plus size={16} className="mr-1" /> Yeni Kullanıcı
          </Button>
        </div>
      </header>
      {ordering && (
        <OrderDialog title="Personel Sıralaması" hint="Bu sıra Kullanıcılar, Rozet Vitrini, Hedefler ve diğer listelerde kullanılır. Sürükleyin veya oklarla taşıyın."
                     items={items.map((u) => ({ id: u.id, label: u.full_name, sub: branches.find((b) => b.id === u.branch_id)?.name || u.role }))}
                     onClose={() => setOrdering(false)}
                     onSave={async (ids) => { try { await http.put("/order/users", { ids }); toast.success("Personel sırası kaydedildi"); setOrdering(false); load(); } catch (e) { toast.error(e?.response?.data?.detail || "Kaydedilemedi"); } }} />
      )}

      <div className="animate-fade-up delay-100">
        <UserTree items={items} branches={branches} onAdd={openNew}
                  a={{ selected, toggleSelect, toggleActive, openEdit, resetPw, remove, me, badges, pins: showPins ? pins : null }} />
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-slate-900 border-slate-800">
          <DialogHeader><DialogTitle>{editing ? "Kullanıcı Düzenle" : "Yeni Kullanıcı"}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="mb-2 block">Personel Fotoğrafı</Label>
              <PhotoField value={form.photo_path} name={form.full_name} onChange={(v) => setForm((f) => ({ ...f, photo_path: v }))} />
            </div>
            <div>
              <Label>Ad Soyad</Label>
              <Input value={form.full_name} onChange={(e) => setForm({ ...form, full_name: e.target.value })}
                     className="bg-slate-950/50 border-slate-700" data-testid="user-form-fullname" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Kullanıcı Adı</Label>
                <Input value={form.username} onChange={(e) => setForm({ ...form, username: e.target.value })}
                       className="bg-slate-950/50 border-slate-700 font-mono"
                       data-testid="user-form-username" />
              </div>
              <div>
                <Label>{editing ? "Yeni PIN" : "PIN"} <span className="text-xs text-slate-500">(6 rakam{editing ? ", opsiyonel" : ""})</span></Label>
                <Input type="text" inputMode="numeric" pattern="[0-9]*" maxLength={6} autoComplete="off" value={form.password} placeholder="••••••"
                       onChange={(e) => setForm({ ...form, password: e.target.value.replace(/\D/g, "").slice(0, 6) })}
                       className="bg-slate-950/50 border-slate-700" data-testid="user-form-password" />
              </div>
            </div>
            <div>
              <Label>E-posta</Label>
              <Input type="email" value={form.email || ""} onChange={(e) => setForm({ ...form, email: e.target.value })}
                     className="bg-slate-950/50 border-slate-700" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Rol</Label>
                <Select value={form.custom_role_id ? `c:${form.custom_role_id}` : form.role} onValueChange={pickRole}>
                  <SelectTrigger className="bg-slate-950/50 border-slate-700" data-testid="user-form-role"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="patron">Patron</SelectItem>
                    <SelectItem value="koordinator">Koordinatör</SelectItem>
                    <SelectItem value="mudur">Şube Müdürü</SelectItem>
                    <SelectItem value="personel">Personel</SelectItem>
                    {roles.map((r) => <SelectItem key={r.id} value={`c:${r.id}`}>{r.name} · {SCOPE_LABELS[r.scope]}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Şube</Label>
                <Select value={form.branch_id || "none"} onValueChange={(v) => setForm({ ...form, branch_id: v === "none" ? "" : v })}>
                  <SelectTrigger className="bg-slate-950/50 border-slate-700" data-testid="user-form-branch"><SelectValue placeholder="Şube seç" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Yok</SelectItem>
                    {branches.map((b) => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <AccessEditor role={form.role} custom={!!customRole} basePerms={customRole?.permissions} perms={form.permissions || {}} onChange={(p) => setForm((f) => ({ ...f, permissions: p }))} />
            {form.role === "patron" && !customRole && (
              <div className="rounded-xl border border-amber-500/30 bg-amber-500/5 p-3 space-y-2">
                <Label className="text-xs text-amber-200 font-mono uppercase tracking-widest">Patron Özel Yetkileri</Label>
                <div className="flex items-center justify-between">
                  <div>
                    <div className="text-sm font-medium">Satış düzenleyebilir</div>
                    <div className="text-[10px] text-slate-500">Varsayılan: kapalı. Açarsanız bu patron herhangi bir satışı değiştirebilir.</div>
                  </div>
                  <Switch checked={!!form.permissions?.can_edit_sales}
                          onCheckedChange={(v) => setForm({ ...form, permissions: { ...(form.permissions || {}), can_edit_sales: v } })}
                          data-testid="user-perm-edit-sales" />
                </div>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} className="border-slate-700">İptal</Button>
            <Button onClick={save} className="bg-blue-600 hover:bg-blue-500" data-testid="user-form-save">Kaydet</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <Dialog open={!!resetInfo} onOpenChange={(o) => !o && setResetInfo(null)}>
        <DialogContent className="bg-slate-900 border-slate-800 max-w-sm" data-testid="reset-pw-dialog">
          <DialogHeader><DialogTitle>Yeni Şifre · {resetInfo?.full_name}</DialogTitle></DialogHeader>
          <p className="text-xs text-amber-300">Bu şifre sadece bir kez gösterilir. Kopyalayıp kullanıcıya iletin.</p>
          <div className="flex gap-2">
            <Input readOnly value={resetInfo?.password || ""} className="font-mono text-lg bg-slate-950/60 border-slate-700" data-testid="reset-pw-value" />
            <Button onClick={() => { navigator.clipboard?.writeText(resetInfo.password); toast.success("Kopyalandı"); }} data-testid="reset-pw-copy"><Copy size={15} /></Button>
          </div>
        </DialogContent>
      </Dialog>
      {bulkPerms && <BulkPermsDialog ids={[...selected]} onClose={() => setBulkPerms(false)} onDone={() => { setBulkPerms(false); load(); }} />}
      <Dialog open={!!allPins} onOpenChange={(o) => !o && setAllPins(null)}>
        <DialogContent className="bg-slate-900 border-slate-800 max-w-lg max-h-[90vh] overflow-y-auto" data-testid="all-pins-dialog">
          <DialogHeader><DialogTitle>Yeni PIN'ler</DialogTitle></DialogHeader>
          <p className="text-xs text-amber-300">Kendi PIN'inizi not edin; bir sonraki girişte bunu kullanacaksınız.</p>
          <div className="divide-y divide-slate-800 rounded-xl border border-slate-800">
            {(allPins || []).map((p) => (
              <div key={p.id} className="flex justify-between px-3 py-2 text-sm"><span>{p.full_name} <span className="font-mono text-slate-500 text-xs">{p.username}</span></span>
                <b className="font-mono tracking-widest" data-testid={`all-pins-${p.username}`}>{p.pin}</b></div>
            ))}
          </div>
          <Button onClick={() => { navigator.clipboard?.writeText((allPins || []).map((p) => `${p.full_name} (${p.username}): ${p.pin}`).join("\n")); toast.success("Liste kopyalandı"); }} data-testid="all-pins-copy">
            <Copy size={15} className="mr-1" /> Listeyi Kopyala
          </Button>
        </DialogContent>
      </Dialog>
    </div>
  );
}
